/**
 * ISO 3166-1 alpha-2 → primary IANA timezone.
 *
 * Used to auto-populate the timezone field on forms when the user picks a
 * country. Curated to single-zone countries (so we never guess wrong for
 * US/CA/RU/AU/BR etc.); multi-zone countries are intentionally omitted and
 * the caller leaves the timezone field for the user to pick.
 *
 * The IDs are IANA names matching what the backend's
 * `TimeZoneInfo.GetSystemTimeZones()` returns on Linux App Service (the
 * production runtime). If you ever need to support Windows-id backends,
 * teach `pickTimezoneForCountry` to do a Windows-id fallback lookup.
 */
export const COUNTRY_PRIMARY_TIMEZONE: Record<string, string> = {
  // GCC + Middle East
  AE: "Asia/Dubai",
  SA: "Asia/Riyadh",
  KW: "Asia/Kuwait",
  BH: "Asia/Bahrain",
  QA: "Asia/Qatar",
  OM: "Asia/Muscat",
  YE: "Asia/Aden",
  IQ: "Asia/Baghdad",
  JO: "Asia/Amman",
  LB: "Asia/Beirut",
  SY: "Asia/Damascus",
  PS: "Asia/Gaza",
  IL: "Asia/Jerusalem",
  TR: "Europe/Istanbul",
  IR: "Asia/Tehran",
  AF: "Asia/Kabul",

  // North Africa
  EG: "Africa/Cairo",
  LY: "Africa/Tripoli",
  TN: "Africa/Tunis",
  DZ: "Africa/Algiers",
  MA: "Africa/Casablanca",
  SD: "Africa/Khartoum",

  // South / Southeast Asia
  IN: "Asia/Kolkata",
  PK: "Asia/Karachi",
  BD: "Asia/Dhaka",
  LK: "Asia/Colombo",
  NP: "Asia/Kathmandu",
  MM: "Asia/Yangon",
  TH: "Asia/Bangkok",
  VN: "Asia/Ho_Chi_Minh",
  PH: "Asia/Manila",
  MY: "Asia/Kuala_Lumpur",
  SG: "Asia/Singapore",
  ID: "Asia/Jakarta",
  HK: "Asia/Hong_Kong",
  TW: "Asia/Taipei",
  JP: "Asia/Tokyo",
  KR: "Asia/Seoul",

  // Europe (single-zone)
  GB: "Europe/London",
  IE: "Europe/Dublin",
  PT: "Europe/Lisbon",
  ES: "Europe/Madrid",
  FR: "Europe/Paris",
  BE: "Europe/Brussels",
  NL: "Europe/Amsterdam",
  LU: "Europe/Luxembourg",
  DE: "Europe/Berlin",
  CH: "Europe/Zurich",
  AT: "Europe/Vienna",
  IT: "Europe/Rome",
  SE: "Europe/Stockholm",
  NO: "Europe/Oslo",
  DK: "Europe/Copenhagen",
  FI: "Europe/Helsinki",
  IS: "Atlantic/Reykjavik",
  PL: "Europe/Warsaw",
  CZ: "Europe/Prague",
  SK: "Europe/Bratislava",
  HU: "Europe/Budapest",
  RO: "Europe/Bucharest",
  BG: "Europe/Sofia",
  GR: "Europe/Athens",
  HR: "Europe/Zagreb",
  RS: "Europe/Belgrade",
  SI: "Europe/Ljubljana",
  EE: "Europe/Tallinn",
  LV: "Europe/Riga",
  LT: "Europe/Vilnius",
  UA: "Europe/Kyiv",
  BY: "Europe/Minsk",
  MD: "Europe/Chisinau",

  // Africa (single-zone for the listed)
  KE: "Africa/Nairobi",
  ET: "Africa/Addis_Ababa",
  UG: "Africa/Kampala",
  TZ: "Africa/Dar_es_Salaam",
  RW: "Africa/Kigali",
  NG: "Africa/Lagos",
  GH: "Africa/Accra",
  ZA: "Africa/Johannesburg",
  ZW: "Africa/Harare",
  ZM: "Africa/Lusaka",
  CI: "Africa/Abidjan",
  SN: "Africa/Dakar",

  // Oceania (single-zone for the listed)
  NZ: "Pacific/Auckland",
  FJ: "Pacific/Fiji",
};

/**
 * Try to pick a sensible timezone for the given country ISO code, restricted to
 * IDs that actually appear in the loaded `available` list (so we never select
 * a string the dropdown can't render).
 *
 * Returns null when:
 *   - the ISO is missing or unknown,
 *   - the country is multi-zone (deliberately omitted from the static map),
 *   - or the mapped IANA id isn't in `available` (e.g., backend filtering).
 *
 * Falls back to `Intl.Locale.getTimeZones()` when the runtime supports it AND
 * the country has exactly one zone — this lets newer browsers cover countries
 * we forgot to add without ever guessing for multi-zone ones.
 */
export function pickTimezoneForCountry(
  iso: string | undefined | null,
  available: ReadonlyArray<{ id: string }>,
): string | null {
  if (!iso) return null;
  const code = iso.trim().toUpperCase();
  if (!code) return null;

  const mapped = COUNTRY_PRIMARY_TIMEZONE[code];
  if (mapped && available.some((t) => t.id === mapped)) return mapped;

  try {
    const locale = new Intl.Locale(`und-${code}`);
    const fn = (locale as unknown as { getTimeZones?: () => string[] }).getTimeZones;
    const tzs = typeof fn === "function" ? fn.call(locale) : [];
    if (Array.isArray(tzs) && tzs.length === 1) {
      const single = tzs[0];
      if (available.some((t) => t.id === single)) return single;
    }
  } catch {
    /* Intl.Locale.getTimeZones() unsupported on this runtime */
  }

  return null;
}
